import { CreatePullRequestDocument } from '@mesa/core/graphql';
import { useQuery, useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { ArrowLeftIcon, CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { octokit } from '@/lib/clients';
import { cn } from '@/lib/utils';
import { installationsQuery } from '@/services/org';

// Define types for GitHub API responses
// interface Installation {
//   id: number;
//   account: {
//     login: string;
//     id: number;
//     [key: string]: any;
//   };
//   [key: string]: any;
// }

interface Repository {
  id: number;
  name: string;
  [key: string]: any;
}

interface Branch {
  name: string;
  [key: string]: any;
}

export const Route = createFileRoute('/_authed/_layout/pulls/new')({
  validateSearch: zodValidator(
    z.object({
      owner: z.string().optional(),
      repo: z.string().optional(),
      base: z.string().optional(),
      head: z.string().optional(),
      title: z.string().optional(),
    })
  ),
  component: NewPullRequestPage,
});

export function NewPullRequestPage() {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();

  const [selectedOwner, setSelectedOwner] = useState<string>(searchParams.owner || '');
  const [selectedRepo, setSelectedRepo] = useState<string>(searchParams.repo || '');
  const [repositories, setRepositories] = useState<{ id: number; name: string }[]>([]);

  // Popover open states
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [repoOpen, setRepoOpen] = useState(false);
  const [baseBranchOpen, setBaseBranchOpen] = useState(false);
  const [compareBranchOpen, setCompareBranchOpen] = useState(false);

  // Form state
  const [title, setTitle] = useState<string>(searchParams.title || '');
  const [body, setBody] = useState<string>('');
  const [baseBranch, setBaseBranch] = useState<string>(searchParams.base || '');
  const [compareBranch, setCompareBranch] = useState<string>(searchParams.head || '');

  // Fetch installations
  const { data: installations = [], isLoading: isLoadingInstallations } = useQuery(installationsQuery());

  // Set initial owner if installations are available
  useEffect(() => {
    if (installations.length > 0) {
      if (searchParams.owner) {
        // Verify the owner exists in installations
        const ownerExists = installations.some((inst) => inst.account.login === searchParams.owner);
        if (ownerExists && selectedOwner !== searchParams.owner) {
          setSelectedOwner(searchParams.owner);
        }
      } else if (!selectedOwner) {
        setSelectedOwner(installations[0].account.login);
      }
    }
  }, [installations, selectedOwner, searchParams.owner]);

  // Fetch repositories for selected owner
  const { data: repos = [], isLoading: isLoadingRepos } = useQuery({
    queryKey: ['repos', selectedOwner],
    queryFn: async () => {
      if (!selectedOwner) return [] as Repository[];

      // Find the installation for the selected owner
      const installation = installations.find((inst) => inst.account.login === selectedOwner);
      if (!installation) return [] as Repository[];

      try {
        const response = await octokit.rest.apps.listInstallationReposForAuthenticatedUser({
          installation_id: installation.id,
        });
        return response.data.repositories as Repository[];
      } catch (error) {
        console.error('Failed to fetch repositories:', error);
        toast.error('Failed to load repositories', {
          description: 'Please try again.',
        });
        return [] as Repository[];
      }
    },
    enabled: !!selectedOwner && installations.length > 0,
  });

  // Update repositories state when repos data changes
  useEffect(() => {
    if (repos.length > 0) {
      setRepositories(
        repos.map((repo) => ({
          id: repo.id,
          name: repo.name,
        }))
      );
    } else {
      setRepositories([]);
    }
  }, [repos]);

  // Load branches when repo changes
  const { data: branches = [], isLoading: isLoadingBranches } = useQuery({
    queryKey: ['branches', selectedOwner, selectedRepo],
    queryFn: async () => {
      if (!selectedOwner || !selectedRepo) return [] as Branch[];

      try {
        const result = await octokit.rest.repos.listBranches({
          owner: selectedOwner,
          repo: selectedRepo,
          per_page: 100,
        });
        return result.data as Branch[];
      } catch (error) {
        console.error('Error fetching branches:', error);
        toast.error('Failed to load branches', {
          description: 'Please try again.',
        });
        return [] as Branch[];
      }
    },
    enabled: !!selectedOwner && !!selectedRepo,
  });

  // When branches load, set default base branch to main or master if available
  useEffect(() => {
    if (branches && branches.length > 0) {
      // If base is specified in search params, use it if valid
      if (searchParams.base) {
        const baseExists = branches.some((b) => b.name === searchParams.base);
        if (baseExists && baseBranch !== searchParams.base) {
          setBaseBranch(searchParams.base);
        }
      } else if (!baseBranch) {
        // Try to find main or master branch for base branch
        const mainBranch = branches.find((b) => b.name === 'main' || b.name === 'master');
        if (mainBranch) {
          setBaseBranch(mainBranch.name);
        } else if (branches.length > 0) {
          // Otherwise use the first branch
          setBaseBranch(branches[0].name);
        }
      }

      // If head is specified in search params, use it if valid
      if (searchParams.head) {
        const headExists = branches.some((b) => b.name === searchParams.head);
        if (headExists && compareBranch !== searchParams.head) {
          setCompareBranch(searchParams.head);
        }
      }
    }
  }, [branches, searchParams.base, searchParams.head, baseBranch, compareBranch]);

  // Get repository node ID for GraphQL mutation
  const { data: repoData } = useQuery({
    queryKey: ['repo-id', selectedOwner, selectedRepo],
    queryFn: async () => {
      if (!selectedOwner || !selectedRepo) return null;
      const response = await octokit.rest.repos.get({
        owner: selectedOwner,
        repo: selectedRepo,
      });
      return response.data;
    },
    enabled: !!selectedOwner && !!selectedRepo,
  });

  // Create pull request mutation
  const { mutate: createPullRequest, isPending } = useMutation({
    mutationFn: async () => {
      if (!selectedOwner || !selectedRepo || !title || !baseBranch || !compareBranch || !repoData) {
        throw new Error('Missing required fields');
      }

      // Use GraphQL to create a draft PR
      const result = await octokit.graphql<{ createPullRequest: { pullRequest: any } }>(
        CreatePullRequestDocument.toString(),
        {
          repositoryId: repoData.node_id,
          baseRefName: baseBranch,
          headRefName: compareBranch,
          title,
          body: body || '',
          draft: true, // Always create as draft
        }
      );

      return result.createPullRequest.pullRequest;
    },
    onSuccess: async (data) => {
      console.log('[PR CREATED] Successfully created PR:', {
        number: data.number,
        url: data.url,
        isDraft: data.isDraft,
        owner: data.repository.owner.login,
        repo: data.repository.name,
        branch: data.headRef.name,
      });

      toast.success('Draft pull request created successfully!');

      // Update branch_pushes table to mark branch as having PR
      try {
        const owner = data.repository.owner.login;
        const repo = data.repository.name;
        const branch = encodeURIComponent(data.headRef.name);

        const response = await fetch(`/api/github/${owner}/${repo}/branch/${branch}/mark-pr-created`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            pullRequestNumber: data.number,
            userId: data.author.databaseId,
          }),
        });

        const result = await response.json();
        console.log('[PR CREATED] Branch update response:', result);
      } catch (error) {
        console.error('[PR CREATED] Failed to update branch status:', error);
        // Don't block navigation on this error
      }

      // Navigate to the new pull request
      navigate({
        to: '/$owner/$repo/pull/$number',
        params: {
          owner: data.repository.owner.login,
          repo: data.repository.name,
          number: data.number,
        },
      });
    },
    onError: (error) => {
      console.error('Error creating pull request:', error);
      toast.error('Failed to create pull request', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createPullRequest();
  };

  const isFormValid =
    selectedOwner && selectedRepo && title && baseBranch && compareBranch && baseBranch !== compareBranch;

  if (isLoadingInstallations) {
    return (
      <div className="container max-w-2xl py-10">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Create New Pull Request</h1>
        </div>
        <div className="py-8 flex justify-center">
          <p className="text-muted-foreground">Loading installations...</p>
        </div>
      </div>
    );
  }

  if (!installations || installations.length === 0) {
    return (
      <div className="container max-w-2xl py-10">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">Create New Pull Request</h1>
            <Button variant="ghost" size="icon" onClick={() => navigate({ to: '/pulls' })}>
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="py-6 text-center">
          <p className="text-muted-foreground">
            No GitHub installations found. Please connect your GitHub account first.
          </p>
          <Button className="mt-4" onClick={() => navigate({ to: '/pulls' })}>
            Return to Pull Requests
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-2xl py-4 px-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Create New Pull Request</h1>
        <p className="text-muted-foreground mt-2">
          Compare changes across branches and create a new draft pull request.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="owner">Owner</Label>
            <Popover open={ownerOpen} onOpenChange={setOwnerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={ownerOpen} className="w-full justify-between">
                  {selectedOwner || 'Select owner'}
                  <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Search owner..." />
                  <CommandList>
                    <CommandEmpty>No owner found.</CommandEmpty>
                    <CommandGroup>
                      {installations.map((org) => (
                        <CommandItem
                          key={org.id}
                          value={org.account.login}
                          onSelect={(value) => {
                            setSelectedOwner(value);
                            setSelectedRepo('');
                            setOwnerOpen(false);
                          }}
                        >
                          <CheckIcon
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedOwner === org.account.login ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          {org.account.login}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="repo">Repository</Label>
            <Popover open={repoOpen} onOpenChange={setRepoOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={repoOpen}
                  className="w-full justify-between"
                  disabled={isLoadingRepos || repositories.length === 0}
                >
                  {selectedRepo || 'Select repository'}
                  <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Search repository..." />
                  <CommandList>
                    <CommandEmpty>No repository found.</CommandEmpty>
                    <CommandGroup>
                      {repositories.map((repo) => (
                        <CommandItem
                          key={repo.id}
                          value={repo.name}
                          onSelect={(value) => {
                            setSelectedRepo(value);
                            setBaseBranch('');
                            setCompareBranch('');
                            setRepoOpen(false);
                          }}
                        >
                          <CheckIcon
                            className={cn('mr-2 h-4 w-4', selectedRepo === repo.name ? 'opacity-100' : 'opacity-0')}
                          />
                          {repo.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="baseBranch">Base branch (target)</Label>
            <Popover open={baseBranchOpen} onOpenChange={setBaseBranchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={baseBranchOpen}
                  className="w-full justify-between"
                  disabled={isLoadingBranches || branches.length === 0}
                >
                  {baseBranch || 'Select base branch'}
                  <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Search base branch..." />
                  <CommandList>
                    <CommandEmpty>No branch found.</CommandEmpty>
                    <CommandGroup>
                      {branches.map((branch) => (
                        <CommandItem
                          key={branch.name}
                          value={branch.name}
                          onSelect={(value) => {
                            setBaseBranch(value);
                            setBaseBranchOpen(false);
                          }}
                        >
                          <CheckIcon
                            className={cn('mr-2 h-4 w-4', baseBranch === branch.name ? 'opacity-100' : 'opacity-0')}
                          />
                          {branch.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {baseBranch === compareBranch && compareBranch && (
              <p className="text-sm text-red-500">Base and compare branches must be different</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="compareBranch">Compare branch (source)</Label>
            <Popover open={compareBranchOpen} onOpenChange={setCompareBranchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={compareBranchOpen}
                  className="w-full justify-between"
                  disabled={isLoadingBranches || branches.length === 0}
                >
                  {compareBranch || 'Select compare branch'}
                  <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Search compare branch..." />
                  <CommandList>
                    <CommandEmpty>No branch found.</CommandEmpty>
                    <CommandGroup>
                      {branches.map((branch) => (
                        <CommandItem
                          key={branch.name}
                          value={branch.name}
                          onSelect={(value) => {
                            setCompareBranch(value);
                            setCompareBranchOpen(false);
                          }}
                        >
                          <CheckIcon
                            className={cn('mr-2 h-4 w-4', compareBranch === branch.name ? 'opacity-100' : 'opacity-0')}
                          />
                          {branch.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter pull request title"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="body">Description</Label>
          <Textarea
            id="body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Enter pull request description"
            rows={6}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate({ to: '/pulls' })}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending || !isFormValid}>
            {isPending ? 'Creating...' : 'Create draft pull request'}
          </Button>
        </div>
      </form>
    </div>
  );
}
